import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import '../../assets/css/views/trainingMaterial.css';
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";


import Loader from "../../components/Loader/loader";
import { Button, ButtonGroup, Card, ContextualMenu, FilePicker, IconFunnel, IconPencil, IconSearch, IconTrash, IconWarningAlt, Modal, OptionInterface, Search, Select, SelectOption, Table, TableColumns, TableData, TextArea, TextBox, Toggle } from "@veneer/core";
import TrainingLibraryService from "../../services/trainingApi/trainingLibrary.service";
import { ITrainingMaterialCategory, ITrainingMaterialCycle, ITrainingMaterialDto, ITrainingMaterialFormats, ITrainingMaterialType } from "../../models/dto/trainingApi/ITrainingMaterialDto";
import LoaderModal from "../../components/Loader/loaderModal";
import { ITrainingMaterialIn, ITrainingMaterialUpdateIn } from "../../models/input/trainingApi/ITrainingMaterialIn";


type SelectValue = number | string | boolean;

interface Pagination {
    currentPage: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
    materials: ITrainingMaterialDto[];
}

interface PaginationProps {
    currentPage: number,
    onPageChange: any,
    pageSize: number,
    totalItems: number,
}

interface FilterProps {
    search: string,
    type: SelectValue[],
    category: SelectValue[],
}

export default function TrainingMaterial() {
    const [t] = useTranslation("global");
    const languageId: any = useSelector((state: RootState) => state.languageId);

    const [loadingModal, setLoadingModal] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [tableData, setTableData] = useState<TableData[]>([]);
    const [tableDataFiltered, setTableDataFiltered] = useState<TableData[]>([]);
    const trainingMaterialData = useRef<ITrainingMaterialDto[]>([]);

    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [search, setSearch] = useState<string>('');
    const [disableSearch, setDisableSearch] = useState<boolean>(true);
    const [searchError, setSearchError] = useState<boolean>(true);
    const appliedFilterRef = useRef<FilterProps>({ search: '', type: [], category: [] });
    const [disableSearchType, setDisableSearchType] = useState<boolean>(true);
    const [disableSearchCategory, setDisableSearchCategory] = useState<boolean>(true);





    const [disableCreate, setDisableCreate] = useState<boolean>(true);

    const [materialId, setMaterialId] = useState<number>(NaN);


    const [showModalCreate, setShowModalCreate] = useState<boolean>(false);
    const [showModalConfirmDeletion, setShowModalConfirmDeletion] = useState<boolean>(false);


    const [nameTraining, setNameTraining] = useState<string>('');
    const [description, setDescription] = useState<string>('');




    const [fileName, setFileName] = useState<string>('');
    const [fileValue, setFileValue] = useState<File | null>(null);
    const [urlFile, setUrlFile] = useState<string | null>(null);
    const [errorFilePicker, setErrorFilePicker] = useState(false);

    const [types, setTypes] = useState<SelectOption[]>([]);
    const [cycles, setCycles] = useState<SelectOption[]>([]);
    const [categories, setCategories] = useState<SelectOption[]>([]);
    const [formats, setFormats] = useState<SelectOption[]>([]);

    const [typeSelected, setTypeSelected] = useState<SelectValue[]>([]);
    const [cycleSelected, setCycleSelected] = useState<SelectValue[]>([]);
    const [categorySelected, setCategorySelected] = useState<SelectValue[]>([]);
    const [formatSelected, setFormatSelected] = useState<SelectValue[]>([]);

    const [disableCreation, setDisableCreation] = useState(true);
    const [disableFilePicker, setDisableFilePicker] = useState(true);

    const [typeFilePicker, setTypeFilePicker] = useState<string>('');

    const [isUpdating, setIsUpdating] = useState<boolean>(false);

    const [link, setLink] = useState<string>('');
    const [errorLink, setErrorLink] = useState<boolean>(false);

    const [typeFilterSelected, setTypeFilterSelected] = useState<SelectValue[]>([]);
    const [categoryFilterSelected, setCategoryFilterSelected] = useState<SelectValue[]>([]);

    enum TrainingMaterialFormats {
        video = 35,
        image = 36,
        pdf = 37,
        link = 1049 //944 ITG - 1049 PROD
    }


    useEffect(() => {
        getTrainingMaterialData(1, appliedFilterRef.current);
        getData();
    }, [])

    useEffect(() => {
        setSearchError(false);
        if (search && search.length > 1) {
            setDisableSearch(false);
        } else {
            setDisableSearch(true);
        }
    }, [search])

    useEffect(() => {
        if (typeFilterSelected.length > 0) {
            setDisableSearchType(false);
        } else {
            setDisableSearchType(true);
        }
    }, [typeFilterSelected])

    useEffect(() => {
        if (categoryFilterSelected.length > 0) {
            setDisableSearchCategory(false);
        } else {
            setDisableSearchCategory(true);
        }
    }, [categoryFilterSelected])

    useEffect(() => {
        if (types.length > 0 && cycles.length > 0 && categories.length > 0 && formats.length > 0) {
            setDisableCreation(false);
        } else {
            setDisableCreation(true);
        }
    }, [types, cycles, categories, formats])

    const isValidLink = useCallback((value: string): boolean => {
        try {
            new URL(value);
            return true;
        } catch {
            return false;
        }
    }, []);

    const isValidFormatUrl = useCallback((): boolean => {
        if (formatSelected.length > 0) {
            if (Number(formatSelected) === TrainingMaterialFormats.link) {
                const isValid = isValidLink(link);
                setErrorLink(!isValid);
                return isValid;
            } else {
                return !!urlFile;
            }
        }
        return false;
    }, [formatSelected, link, urlFile, isValidLink]);

    useEffect(() => {
        const validFormatUrl = isValidFormatUrl();

        const isValidForm =
            nameTraining.length >= 3 &&
            typeSelected.length > 0 &&
            cycleSelected.length > 0 &&
            categorySelected.length > 0 &&
            formatSelected.length > 0 &&
            validFormatUrl;

        setDisableCreate(!isValidForm);
    }, [
        nameTraining,
        typeSelected,
        cycleSelected,
        categorySelected,
        formatSelected,
        isValidFormatUrl,
    ]);

    useEffect(() => {
        if (formatSelected.length > 0) {
            setDisableFilePicker(false);
            if (Number(formatSelected) === TrainingMaterialFormats.video) {
                setTypeFilePicker('.mp4')
            } else if (Number(formatSelected) === TrainingMaterialFormats.image) {
                setTypeFilePicker('.jpg,.jpeg,.png')

            } else if (Number(formatSelected) === TrainingMaterialFormats.pdf) {
                setTypeFilePicker('.pdf')

            } else {
                setTypeFilePicker('')
            }
        } else {
            setDisableFilePicker(true);
        }
    }, [formatSelected])

    useEffect(() => {
        if (fileValue) {
            const formData = new FormData();
            formData.append("formFile", fileValue);
            try {
                setLoadingModal(true);
                TrainingLibraryService.postFiles(formData)
                    .then(r => {
                        if (r.path) {
                            setUrlFile(r.path);
                            setLoadingModal(false);
                        } else {
                            setLoadingModal(false);
                            setErrorFilePicker(true);
                        }
                    })
            } catch (error) {
                setLoadingModal(false);
                setErrorFilePicker(true);
            }
        }
    }, [fileValue])


    const getTrainingMaterialData = (pageNumber: number, filter: FilterProps) => {
        setLoading(true);
        const type = filter?.type?.length > 0 ? Number(filter.type) : null;
        const category = filter?.category?.length > 0 ? Number(filter.category) : null;
        try {
            TrainingLibraryService.getMaterialsData(languageId.value, pageNumber, 50, filter.search, type, category)
                .then((r: Pagination) => {
                    setPaginationProps((prevProps) => ({
                        ...prevProps,
                        currentPage: r.currentPage,
                        pageSize: r.pageSize,
                        totalItems: r.totalRecords
                    }));

                    fillTrainingMaterialData(r.materials);
                })
        } catch (error) {
            console.error(error);
        }
    }

    const handlePageChange = (pageNumber: number) => {
        getTrainingMaterialData(pageNumber, appliedFilterRef.current);
    }

    const [paginationProps, setPaginationProps] = useState<PaginationProps>({ currentPage: 1, onPageChange: handlePageChange, pageSize: NaN, totalItems: NaN });


    const fillTrainingMaterialData = (data: ITrainingMaterialDto[]) => {
        const pagedData: TableData[] = [];
        for (let i = 0; i < data.length; i++) {
            const material: ITrainingMaterialDto = data[i];
            let tableData: TableData = {};
            tableData = {
                'materialID': material.materialID,
                'materialName': material.materialName,
                'materialUrl': material.materialUrl,
                'cycleID': material.cycleID,
                'cycle': material.cycle,
                'materialTypeID': material.materialTypeID,
                'materialType': material.materialType,
                'categoryID': material.categoryID,
                'category': material.category,
                'subcategoriesIDs': material.subcategoriesIDs,
                'subcategory': material.subcategory,
                'description': material.description,
                'dateCreated': material.dateCreated,
                'userID': material.userID,
                'userTrainer': material.userTrainer,
                'formatID': material.formatID,
                'active': <Toggle id={"toggle" + material.materialID} defaultOn={material.active} onChange={() => handleChangeActive(material.materialID, !material.active)} />,
                rowConfig: {
                    action: <ContextualMenu
                        onClick={handleActionRowConfig}
                        options={[
                            { value: material.materialID, label: t("header.edit"), icon: <IconPencil /> },
                            { value: material.materialID, label: t("header.delete"), icon: <IconTrash /> },
                        ]}
                        placement="leading"
                    />,
                    disabled: false,
                    noWrap: false,
                    selected: false,
                }

            }
            pagedData.push(tableData);
        }
        setTableData(pagedData);
        setTableDataFiltered(pagedData);
        trainingMaterialData.current = data;
        setLoading(false);
    }


    const getData = async () => {
        try {
            const typesList = await TrainingLibraryService.getMaterials(languageId.value);
            const resultTypes = fillDataTypes(typesList);
            setTypes(resultTypes);

            const categoriesList = await TrainingLibraryService.getCategories(languageId.value);
            const resultCategories = fillCategories(categoriesList);
            setCategories(resultCategories);

            const cyclesList = await TrainingLibraryService.getCycles();
            const resultCycles = fillCycles(cyclesList);
            setCycles(resultCycles);

            const formatsList = await TrainingLibraryService.getFormats(languageId.value);
            const resultFormats = fillFormats(formatsList);
            setFormats(resultFormats);

        } catch (error) {
            console.error(error)

        }
    }

    const fillDataTypes = (data: ITrainingMaterialType[]) => {
        const dataTemp: SelectOption[] = [];
        for (let i = 0; i < data.length; i++) {
            const camp: ITrainingMaterialType = data[i];
            let selectData: SelectOption;
            selectData = {
                value: camp.materialTypeID,
                label: camp.materialType,
            };
            dataTemp.push(selectData);
        }
        return dataTemp;
    };

    const fillCategories = (data: ITrainingMaterialCategory[]) => {
        const dataTemp: SelectOption[] = [];
        for (let i = 0; i < data.length; i++) {
            const camp: ITrainingMaterialCategory = data[i];
            let selectData: SelectOption;
            selectData = {
                value: camp.categoryID,
                label: camp.category,
            };
            dataTemp.push(selectData);
        }
        return dataTemp;
    };

    const fillCycles = (data: ITrainingMaterialCycle[]) => {
        const dataTemp: SelectOption[] = [];
        for (let i = 0; i < data.length; i++) {
            const camp: ITrainingMaterialCycle = data[i];
            let selectData: SelectOption;
            selectData = {
                value: camp.cycleID,
                label: camp.cycleName,
            };
            dataTemp.push(selectData);
        }
        return dataTemp;
    };

    const fillFormats = (data: ITrainingMaterialFormats[]) => {
        const dataTemp: SelectOption[] = [];
        for (let i = 0; i < data.length; i++) {
            const camp: ITrainingMaterialFormats = data[i];
            let selectData: SelectOption;
            selectData = {
                value: camp.fieldID,
                label: camp.fieldValue,
            };
            dataTemp.push(selectData);
        }
        return dataTemp;
    };


    const handleChangeActive = (materialId: number, active: boolean) => {
        setLoading(true)
        TrainingLibraryService.updateTrainingMaterialActive(materialId, active)
            .then(r => {
                getTrainingMaterialData(1, appliedFilterRef.current);
            })
            .catch(error => {
                setLoading(false);
                console.error("Error updating training material status:", error);
            });
    }

    const handleActionRowConfig = (e: React.SyntheticEvent, option?: OptionInterface) => {
        if (option) {
            if (option.label === "Edit" || option.label === "Editar" || option.label === "Editar") {
                setIsUpdating(true);
                const trainingMaterialTemp = trainingMaterialData.current.find(item => item.materialID === Number(option.value));
                if (trainingMaterialTemp) {
                    setLoading(true);
                    setMaterialId(Number(option.value));
                    chargeDataToUpdate(trainingMaterialTemp)
                    setShowModalCreate(true);
                }
            } else if (option.label === "Delete" || option.label === "Eliminar" || option.label === "Excluir") {
                setMaterialId(Number(option.value));
                setShowModalConfirmDeletion(true);
            }

        }
    }

    const handleSearch = () => {
        if (search) {
            appliedFilterRef.current.search = search;
            getTrainingMaterialData(1, appliedFilterRef.current);
        } else {
            setSearchError(true)
        }
    }

    const onClearSearch = () => {
        setSearch('');
        appliedFilterRef.current.search = '';
        getTrainingMaterialData(1, appliedFilterRef.current);
    }


    const handleSearchType = () => {
        if (typeFilterSelected.length > 0) {
            appliedFilterRef.current.type = typeFilterSelected;
            getTrainingMaterialData(1, appliedFilterRef.current);
        }
    }

    const onClearSearchType = () => {
        setTypeFilterSelected([]);
        appliedFilterRef.current.type = [];
        getTrainingMaterialData(1, appliedFilterRef.current);
    }

    const handleSearchCategory = () => {
        if (categoryFilterSelected.length > 0) {
            appliedFilterRef.current.category = categoryFilterSelected;
            getTrainingMaterialData(1, appliedFilterRef.current);
        }
    }

    const onClearSearchCategory = () => {
        setCategoryFilterSelected([]);
        appliedFilterRef.current.category = [];
        getTrainingMaterialData(1, appliedFilterRef.current);
    }

    const columns: TableColumns[] = [
        {
            id: 'materialName',
            label: t("header.materialName"),
            filter: (
                <div className="trainingMaterialTableFilters">
                    <Search
                        label="Search"
                        id="table-filter-training-material"
                        value={search}
                        onChange={value => setSearch(String(value))}
                        error={searchError}
                        onClear={onClearSearch}
                    />
                    <Button disabled={disableSearch} className="buttonSearchTrainingMaterial" onClick={handleSearch} leadingIcon={<IconSearch />} />

                </div>
            ),
        },
        {
            id: 'materialUrl',
            label: t("header.materialUrl"),
        },
        {
            id: 'materialType',
            label: t("header.materialType"),
            filter: (
                <div className="trainingMaterialTableFilters">
                    <Select
                        className="trainingMaterialSelect"
                        label={t("header.type")}
                        onChange={e => setTypeFilterSelected([e.value])}
                        onClear={onClearSearchType}
                        options={types}
                        value={typeFilterSelected}
                        placement="bottom-start"
                        disabled={disableCreation}
                    />
                    <Button disabled={disableSearchType} className="buttonSearchTrainingMaterial" onClick={handleSearchType} leadingIcon={<IconSearch />} />

                </div>
            ),
        },
        {
            id: 'category',
            label: t("header.category"),
            filter: (
                <div className="trainingMaterialTableFilters">
                    <Select
                        className="trainingMaterialSelect"
                        label={t("header.category")}
                        onChange={e => setCategoryFilterSelected([e.value])}
                        onClear={onClearSearchCategory}
                        options={categories}
                        value={categoryFilterSelected}
                        placement="bottom-start"
                        disabled={disableCreation}
                    />
                    <Button disabled={disableSearchCategory} className="buttonSearchTrainingMaterial" onClick={handleSearchCategory} leadingIcon={<IconSearch />} />

                </div>
            ),
        },
        {
            id: 'description',
            label: t("header.description"),
        },
        {
            id: 'active',
            label: t("header.active"),
        },
        {
            id: 'cycle',
            label: t("header.cycle"),
        },
        {
            id: 'dateCreated',
            label: t("header.createdDate"),
        },
        {
            id: 'userTrainer',
            label: t("header.trainer"),
        },

    ];

    const chargeDataToUpdate = (trainingMaterialData: TableData) => {
        setNameTraining(trainingMaterialData.materialName)
        setTypeSelected([trainingMaterialData.materialTypeID])
        setCycleSelected([trainingMaterialData.cycleID])
        setCategorySelected([trainingMaterialData.categoryID])
        setDescription(trainingMaterialData.description)
        setFormatSelected([trainingMaterialData.formatID])

        if (Number(trainingMaterialData.formatID) === TrainingMaterialFormats.link) {
            setLink(trainingMaterialData.materialUrl)
        } else {
            setUrlFile(trainingMaterialData.materialUrl)
            setFileName('file')
        }

        setLoading(false);
    }



    const onCreateTrainingMaterial = () => {
        setLoadingModal(true);
        const createdDate = new Date().toISOString();

        const urlMaterial = Number(formatSelected) === TrainingMaterialFormats.link ? link : urlFile;

        let trainingMaterialIn: ITrainingMaterialIn = {
            materialName: nameTraining,
            format: Number(formatSelected),
            materialUrl: urlMaterial!,
            materialTypeID: Number(typeSelected),
            cycleID: Number(cycleSelected),
            categoryID: Number(categorySelected),
            description: description,
            createdData: createdDate,
            active: true
        }
        try {
            TrainingLibraryService.postTrainingMaterial(trainingMaterialIn)
                .then(r => {
                    setLoadingModal(false);
                    setShowModalCreate(false);
                    cleanForm();
                    getTrainingMaterialData(1, appliedFilterRef.current);

                })

        } catch (error) {
            console.error(error);
            setLoadingModal(false);
            setShowModalCreate(false);
            cleanForm();
        }

    }


    const onUpdateTrainingMaterial = () => {
        setLoadingModal(true);

        const urlMaterial = Number(formatSelected) === TrainingMaterialFormats.link ? link : urlFile;
        let trainingMaterialUpdateIn: ITrainingMaterialUpdateIn = {
            materialID: Number(materialId),
            materialName: nameTraining,
            format: Number(formatSelected),
            materialUrl: urlMaterial!,
            materialTypeID: Number(typeSelected),
            cycleID: Number(cycleSelected),
            categoryID: Number(categorySelected),
            description: description,
        }
        try {
            TrainingLibraryService.updateTrainingMaterial(trainingMaterialUpdateIn)
                .then(r => {
                    setLoadingModal(false);
                    setShowModalCreate(false);
                    cleanForm();
                    setIsUpdating(false);
                    setMaterialId(NaN);
                    getTrainingMaterialData(1, appliedFilterRef.current);

                })

        } catch (error) {
            console.error(error);
            setLoadingModal(false);
            setShowModalCreate(false);
            cleanForm();
            setIsUpdating(false);
            setMaterialId(NaN);
        }

    }

    const onCancelCreate = () => {
        setShowModalCreate(false);
        cleanForm();
    }

    const onCancelUpate = () => {
        setShowModalCreate(false);
        cleanForm();
        setIsUpdating(false);
        setMaterialId(NaN);
    }


    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const file = e.target.files[0];
            const maxSize = 5 * 1024 * 1024; // 5 MB

            if (file.size > maxSize) {
                setErrorFilePicker(true);
                setFileValue(null);
                setFileName('');
            } else {
                setErrorFilePicker(false);
                setFileValue(file);
                const nameTemp = cleanFileName(file.name);
                setFileName(nameTemp);
            }
        }
    };

    const onClearFilePicker = () => {
        setFileValue(null);
        setFileName('');
        setUrlFile('');
    };

    function cleanFileName(fileName: string): string {
        let cleanedName = fileName.replace(/\s+/g, '_');
        cleanedName = cleanedName.replace(/[^\w\-\.]/g, '');
        return cleanedName;
    }

    const confirmDeletion = () => {
        setLoading(true);
        TrainingLibraryService.deleteTrainingMaterial(materialId)
            .then((r: any) => {
                setLoading(true);
                getTrainingMaterialData(1, appliedFilterRef.current);

                setShowModalConfirmDeletion(false);
                setMaterialId(NaN);
            })
            .catch((error: Error) => {
                setLoading(false);
                setShowModalConfirmDeletion(false);
                setMaterialId(NaN);
                console.error("Error deleting training material: ", error);
            });
    }
    const cancelDeletion = () => {
        setMaterialId(NaN);
        setShowModalConfirmDeletion(false);
    }

    const cleanForm = () => {
        setNameTraining('');
        setTypeSelected([]);
        setCycleSelected([]);
        setCategorySelected([]);
        setDescription('');
        setFormatSelected([]);
        setFileValue(null);
        setFileName('');
        setUrlFile('');
        setLink('')
    }




    return (
        <div className="trainingMaterial">
            {loading && <Loader />}

            <h4 className="trainingMaterialTitle">{t("header.trainingMaterial")}</h4>
            <div className="trainingMaterialtop">
                <div className="buttonsTrainingMaterialtop">
                    <Button className="buttonFiltersTrainingMaterial" appearance="secondary" onClick={() => setShowFilters(!showFilters)} leadingIcon={<IconFunnel />} />
                    <Button disabled={disableCreation} className="buttonCreateTrainingMaterial" onClick={() => setShowModalCreate(true)}   >{t("header.createTrainingMaterial")}</Button>
                </div>
            </div>
            <Card
                appearance="outlined"
                className="trainingMaterialCard"
                content={<>
                    <Table
                        className="trainingMaterialTable"
                        behavior={{
                            type: 'singlePage',
                        }}
                        showFilters={showFilters}
                        columnResize
                        loading={loading}
                        columns={columns}
                        data={tableDataFiltered}
                        loadingDataLength={10}
                        pagination={paginationProps}
                        customNoItems={<div className="customNoItems"><div className="DivIconCustomNoItems"><IconWarningAlt className="IconCustomNoItems" /></div>{t("header.noItemsFound")}</div>}
                    />
                </>
                }
            />

            {loadingModal && <LoaderModal />}
            <div style={{ position: 'relative' }}>
                <Modal
                    className="modalCreateTrainingMaterial"
                    align="center"
                    id="modalCreateTrainingMaterial"
                    show={showModalCreate}
                    onClose={() => isUpdating ? onCancelUpate() : onCancelCreate()}
                    closeButton
                    footer={(
                        <ButtonGroup className="modalCreateTrainingMaterialButtonGroup">
                            <Button disabled={disableCreate} onClick={() => isUpdating ? onUpdateTrainingMaterial() : onCreateTrainingMaterial()} >{isUpdating ? t("header.updateTrainingMaterial") : t("header.createTrainingMaterial")}</Button>
                            <Button onClick={() => isUpdating ? onCancelUpate() : onCancelCreate()} appearance="secondary">{t("header.cancel")}</Button>
                        </ButtonGroup>
                    )}
                    title={isUpdating ? t("header.updateTrainingMaterial") : t("header.createTrainingMaterial")}
                >
                    <div>

                        <TextBox
                            className="trainingMaterialTextbox"
                            label={t("header.name")}
                            value={nameTraining}
                            onChange={value => setNameTraining(value)}
                            required
                        />
                        <Select
                            className="trainingMaterialSelect"
                            label={t("header.type")}
                            onChange={e => setTypeSelected([e.value])}
                            onClear={() => setTypeSelected([])}
                            options={types}
                            value={typeSelected}
                            placement="bottom-start"
                            required
                            disabled={disableCreation}
                        />

                        <Select
                            className="trainingMaterialSelect"
                            label={t("header.cycle")}
                            onChange={e => setCycleSelected([e.value])}
                            onClear={() => setCycleSelected([])}
                            options={cycles}
                            value={cycleSelected}
                            placement="bottom-start"
                            required
                            disabled={disableCreation}
                        />

                        <Select
                            className="trainingMaterialSelect"
                            label={t("header.category")}
                            onChange={e => setCategorySelected([e.value])}
                            onClear={() => setCategorySelected([])}
                            options={categories}
                            value={categorySelected}
                            placement="bottom-start"
                            required
                            disabled={disableCreation}
                        />

                        <TextArea
                            className="trainingMaterialTextarea"
                            id="trainingMaterialTextarea"
                            label={t("header.description")}
                            type="text"
                            onChange={setDescription}
                            value={description}
                        />

                        <Select
                            className="trainingMaterialSelect"
                            label={t("header.fileType")}
                            onChange={e => setFormatSelected([e.value])}
                            onClear={() => setFormatSelected([])}
                            options={formats}
                            value={formatSelected}
                            placement="bottom-start"
                            required
                            disabled={disableCreation}
                        />

                        {formatSelected.length > 0 ?
                            Number(formatSelected) === TrainingMaterialFormats.link ?
                                <TextBox
                                    className="trainingMaterialTextbox"
                                    label={t("header.url")}
                                    value={link}
                                    onChange={value => setLink(value)}
                                    required
                                    error={errorLink}
                                    helperText={t("header.errorTrainingMaterialLink")}
                                />
                                :
                                <FilePicker
                                    className="trainingMaterialFilepicker"
                                    id="trainingMaterial-file-picker"
                                    label={t("header.uploadFile")}
                                    accept={typeFilePicker}
                                    onChange={handleFileChange}
                                    onClear={onClearFilePicker}
                                    value={fileName}
                                    error={errorFilePicker}
                                    helperText={t("header.errorFilePickerTrainingMaterial")}
                                    disabled={disableFilePicker}
                                    required
                                />
                            : null
                        }

                    </div>

                </Modal>
            </div>

            <div style={{ position: 'relative' }}>
                <Modal
                    className="modalDeleteTrainingMaterial"
                    align="center"
                    id="modalDeleteTrainingMaterial"
                    show={showModalConfirmDeletion}
                    footer={(
                        <ButtonGroup className="modalTrainingMaterialConfirmDeletionButtonGroup">
                            <Button onClick={() => confirmDeletion()}>{t("header.confirm")}</Button>
                            <Button onClick={() => cancelDeletion()} appearance="secondary">{t("header.cancel")}</Button>
                        </ButtonGroup>
                    )}
                    title={t("header.deleteTrainingMaterial")}
                >
                    <IconTrash size={120} />
                    <br />
                    <b>{t("header.messageDeleteTrainingMaterial")}</b>

                </Modal>
            </div>




        </div>
    );
}